import { WORKFLOW_ANALYTICS_URL_NAMESPACE } from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { URL_PARAM_KEYS } from "@/utils/utils";

export const WORKFLOW_VERSION_TRACES_SEARCH_CONTEXT = "workflowVersionTraces";

export interface WorkflowVersionTracesSearchContext {
  workflowId: number;
  workflowVersionNumber: number;
}

/** Params owned by workflow Analytics; cleared only when switching workflow/version. */
const SCOPED_SEARCH_PARAM_KEYS = [
  URL_PARAM_KEYS.SEARCH_CONTEXT,
  URL_PARAM_KEYS.SEARCH_CONTEXT_WORKFLOW_ID,
  URL_PARAM_KEYS.SEARCH_CONTEXT_WORKFLOW_VERSION_NUMBER,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.filters,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.q,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.page,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.pageSize,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.sortBy,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.sortDesc,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.startTime,
  WORKFLOW_ANALYTICS_URL_NAMESPACE.endTime,
];

export const parseWorkflowVersionTracesSearchContext = (
  params: URLSearchParams,
): WorkflowVersionTracesSearchContext | null => {
  if (params.get(URL_PARAM_KEYS.SEARCH_CONTEXT) !== WORKFLOW_VERSION_TRACES_SEARCH_CONTEXT) {
    return null;
  }

  const workflowId = Number(params.get(URL_PARAM_KEYS.SEARCH_CONTEXT_WORKFLOW_ID));
  const workflowVersionNumber = Number(
    params.get(URL_PARAM_KEYS.SEARCH_CONTEXT_WORKFLOW_VERSION_NUMBER),
  );
  if (!Number.isFinite(workflowId) || !Number.isFinite(workflowVersionNumber)) {
    return null;
  }

  return { workflowId, workflowVersionNumber };
};

export const isWorkflowVersionTracesSearchContextValid = (
  params: URLSearchParams,
  context: WorkflowVersionTracesSearchContext,
): boolean => {
  const stored = parseWorkflowVersionTracesSearchContext(params);
  return (
    !!stored &&
    stored.workflowId === context.workflowId &&
    stored.workflowVersionNumber === context.workflowVersionNumber
  );
};

const writeSearchContextPointer = (
  params: URLSearchParams,
  context: WorkflowVersionTracesSearchContext,
): void => {
  params.set(URL_PARAM_KEYS.SEARCH_CONTEXT, WORKFLOW_VERSION_TRACES_SEARCH_CONTEXT);
  params.set(URL_PARAM_KEYS.SEARCH_CONTEXT_WORKFLOW_ID, String(context.workflowId));
  params.set(
    URL_PARAM_KEYS.SEARCH_CONTEXT_WORKFLOW_VERSION_NUMBER,
    String(context.workflowVersionNumber),
  );
};

/** First visit: stamp searchContext without touching traces table/sidebar namespaces. */
export const migrateWorkflowVersionTracesSearchContextParams = (
  params: URLSearchParams,
  context: WorkflowVersionTracesSearchContext,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  writeSearchContextPointer(next, context);
  return next;
};

/** Version/workflow change: drop scoped Analytics state and write a fresh context pointer. */
export const resetWorkflowVersionTracesSearchContextParams = (
  params: URLSearchParams,
  context: WorkflowVersionTracesSearchContext,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  for (const key of SCOPED_SEARCH_PARAM_KEYS) {
    next.delete(key);
  }
  writeSearchContextPointer(next, context);
  return next;
};
